import { useDebounceCallback } from "hooks/useDebounce";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState
} from "react";
import styled from "styled-components";

interface Props {
  targetSelector: string;
  parentSelector: string;
  debounceTimeout?: number;
  children: ReactNode;
}

export type TargetElementInfo = {
  top: number;
  left: number;
  height: number;
  width: number;
};

/**
 * Use absolute positioning so the element "sticks" to the button even when scrolling.
 *
 * Top distance is the preview buttons distance from it's parent plus the preview buttons height.
 *
 * Right distance is the parent containers width minus the preview buttons distance to the left
 * plus the preview buttons width.
 *
 */
const StyledDiv = styled.div<{
  top: number;
  left: number;
  height: number;
  width: number;
}>`
  position: absolute;
  top: ${props => props.top + props.height + 8}px;
  right: calc(100% - ${props => props.left + props.width}px);
  z-index: 5000;
`;

const AdjacentPopup = ({
  targetSelector,
  parentSelector,
  children,
  debounceTimeout = 250
}: Props) => {
  const [currPos, setCurrPos] = useState<TargetElementInfo | null>(null);

  const onResize = useDebounceCallback(
    (targetSelector: string, parentSelector: string) => {
      setTargetInfo(setCurrPos, targetSelector, parentSelector);
    },
    debounceTimeout
  );

  useEffect(() => {
    const resizeListener = new ResizeObserver(() =>
      onResize(targetSelector, parentSelector)
    );

    setTargetInfo(setCurrPos, targetSelector, parentSelector);
    resizeListener.observe(document.body);

    return () => {
      resizeListener.disconnect();
    };
  }, [parentSelector, targetSelector, setCurrPos, onResize]);

  if (!currPos) {
    return null;
  }

  return (
    <StyledDiv
      top={currPos.top}
      left={currPos.left}
      height={currPos.height}
      width={currPos.width}
    >
      {children}
    </StyledDiv>
  );
};

export const setTargetInfo = (
  set: Dispatch<SetStateAction<TargetElementInfo | null>>,
  targetSelector: string,
  parentSelector: string
) => {
  const element = document.querySelector<Element>(targetSelector);

  const parent = document.querySelector(parentSelector);

  if (element && parent) {
    const {
      top: previewButtonTop,
      left,
      height,
      width
    } = element.getBoundingClientRect();

    const { top: parentTop, left: parentLeft } = parent.getBoundingClientRect();

    set({
      top: previewButtonTop - parentTop,
      left: left - parentLeft,
      height,
      width
    });

    return true;
  }

  return false;
};

export default AdjacentPopup;
